import React from 'react';
import { Box, Typography } from '@mui/material';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { CollaborativeAgreementRejectionReason, License } from '@uplift/api';
import { useRejectProviderLicenseCollabAgreementMutation } from '@uplift/queries';

import { useAlert } from '../../AlertProvider';
import { Button } from '../../Button';
import { Select, SelectItem } from '../../Inputs/Select';
import { TextInput } from '../../Inputs/TextInput';
import { Modal, ModalActions, ModalContent } from '../../Modal';
import { collaborativeAgreementRejectionReasonLabels } from '../utils';

type RejectCollabAgreementFormValues = {
  rejectionReason: CollaborativeAgreementRejectionReason | '';
  rejectionDetails: string;
};

const rejectCollabAgreementValidationSchema = Yup.object().shape({
  rejectionReason: Yup.string().required('Select a rejection reason'),
  rejectionDetails: Yup.string().when('rejectionReason', {
    is: 'other',
    then: schema => schema.trim().required('Add details for the rejection'),
    otherwise: schema => schema.notRequired(),
  }),
});

export type RejectCollabAgreementModalProps = {
  onClose: () => void;
  license: Pick<License, 'id' | 'userId' | 'stateId'>;
  collabAgreementId: string;
};

export const RejectCollaborativeAgreementModal: React.VFC<RejectCollabAgreementModalProps> = ({ onClose, license, collabAgreementId }) => {
  const alert = useAlert();
  const rejectCollabAgreementMutation = useRejectProviderLicenseCollabAgreementMutation(license.userId);

  const onSubmit = async (values: RejectCollabAgreementFormValues) => {
    if (!values.rejectionReason) return;

    await rejectCollabAgreementMutation.mutateAsync({
      collabAgreementId: collabAgreementId,
      data: {
        rejectionReason: values.rejectionReason,
        rejectionDetails: values.rejectionDetails.trim() || undefined,
      },
    });
    alert.success('Collaborative agreement rejected');
    onClose();
  };

  return (
    <Formik<RejectCollabAgreementFormValues>
      initialValues={{ rejectionReason: '', rejectionDetails: '' }}
      validationSchema={rejectCollabAgreementValidationSchema}
      onSubmit={onSubmit}
    >
      {formik => (
        <Modal open title="Reject Collaborative Agreement" maxWidth="md" fullWidth showCloseIcon onClose={onClose}>
          <ModalContent>
            <form id="reject-collab-agreement-form" onSubmit={formik.handleSubmit}>
              <Box display="flex" flexDirection="column" gap={3}>
                <Typography variant="body2">
                  The provider will be notified and asked to upload a new agreement. Rejection cannot be undone.
                </Typography>
                <Select name="rejectionReason" label="Reason for rejection">
                  {Object.entries(collaborativeAgreementRejectionReasonLabels).map(([ value, label ]) => (
                    <SelectItem key={value} value={value}>{label}</SelectItem>
                  ))}
                </Select>
                <TextInput
                  name="rejectionDetails"
                  label={formik.values.rejectionReason === 'other' ? 'Details (required)' : 'Details (optional)'}
                  placeholder="Add any additional context for the provider..."
                  fullWidth
                  multiline
                  minRows={2}
                />
              </Box>
            </form>
          </ModalContent>
          <ModalActions>
            <Button variant="outlined" color="secondary" disabled={formik.isSubmitting} onClick={onClose}>Cancel</Button>
            <Button variant="contained" color="error" disabled={formik.isSubmitting} type="submit" form="reject-collab-agreement-form">
              Reject Agreement
            </Button>
          </ModalActions>
        </Modal>
      )}
    </Formik>
  );
};
